import { useLocation } from "react-router-dom";
import { useAppSelector } from "utils/redux";
import { useQuery, useQueryParams } from "@repo/ping-react-js";

export const getReadonlyTokenFromCookie = (): string | undefined => {
  const cookies = document.cookie.split(";");
  const readonlyCookie = cookies.find((cookie) =>
    cookie.trim().startsWith("maps_readonly_token="),
  );
  if (readonlyCookie) {
    let token = readonlyCookie.split("=")[1].trim();
    if (token.startsWith("Bearer ")) {
      token = token.substring(7);
    }
    return token;
  }
  return undefined;
};

export const useReadOnlyToken = (): string | undefined => {
  const accessToken = useAppSelector((state) => state.auth.accessToken);
  const query = useQuery();
  const urlToken = query.get("token");
  const cookieToken = getReadonlyTokenFromCookie();
  const readOnlyToken = urlToken || cookieToken;

  return accessToken ? undefined : readOnlyToken;
};

export const useItemKey = () => {
  const query = useQuery();
  // Resolve the URL id through useSovid so pingid routes return a real sovid
  // instead of the raw "p-…" string. The item-key fallback is left untouched —
  // item keys were generated against the underlying sovid and already contain it.
  const resolvedFromUrl = useSovid();
  const sov_id_query_parameter =
    resolvedFromUrl === "pli" ? undefined : resolvedFromUrl;
  const itemKey = query.get("item_key") || query.get("itemKey") || "";
  const sheet_row_number_query_parameter = query.get("srn");
  const sheet_name_query_parameter = query.get("sn");

  const [orig_sov_id_item_key, sn_item_key, srn_item_key] = itemKey.split("!");
  const sov_id_item_key = orig_sov_id_item_key.replace("i-", "");

  return {
    sovId: sov_id_item_key || sov_id_query_parameter,
    sn: sn_item_key || sheet_name_query_parameter,
    srn: srn_item_key || sheet_row_number_query_parameter,
  };
};

/**
 * Returns the resolved sovid for use in API calls. For pingid URLs (p-xxx),
 * waits for the environment endpoint to resolve pingid → sovid. For regular
 * sovid URLs, returns the value immediately.
 *
 * The `/maps/pli` route is a static path (not `/maps/:sovid`), so neither
 * useParams nor the query string carries a sovid. Return the legacy "pli"
 * placeholder so callers don't each have to special-case PLI mode.
 */
export const useSovid = (): string | null => {
  const { isPLI } = usePLI();
  const { sovid: rawSovid } = useQueryParams("sovid");
  const resolvedSovid = useAppSelector((state) => state.settings.resolvedSovid);
  const resolvedFor = useAppSelector((state) => state.settings.resolvedFor);

  if (isPLI) return "pli";
  if (!rawSovid) return null;

  // Pingids start with "p-" — wait for the env endpoint to resolve, and only
  // trust the cached resolution if it was for the id currently in the URL.
  if (rawSovid.startsWith("p-")) {
    return resolvedFor === rawSovid ? resolvedSovid : null;
  }

  // Regular sovids, "activity", etc. — use directly
  return rawSovid;
};

export const useAuth = () => {
  const accessToken = useAppSelector((state) => state.auth.accessToken);
  return { accessToken };
};

export const usePLI = () => {
  const { sovid } = useQueryParams("sovid");
  const location = useLocation();
  // Check both query params and pathname for PLI routes
  const isPLI =
    (sovid && sovid === "pli") || location.pathname.endsWith("/pli");

  return { isPLI };
};

export const usePingId = () => {
  const { sovid } = useQueryParams("sovid");
  const resolvedPingid = useAppSelector(
    (state) => state.settings.resolvedPingid,
  );
  const { isPLI } = usePLI();

  if (isPLI) return null;

  if (resolvedPingid) {
    return resolvedPingid;
  }

  return sovid;
};

/** Returns true when a real pingid (p- prefixed) is available, gating pingid-only features like cat modeling. */
export const useHasPingId = (): boolean => {
  const { isPLI } = usePLI();
  const pingId = usePingId();

  if (isPLI) return false;
  return Boolean(pingId?.startsWith("p-"));
};
